import { EditableText, Subheading, Switch, SwitchField } from '@criteria-labs/react'
import { type Endpoint } from '@criteria-labs/docs-kit'
import debounce from 'lodash.debounce'
import { useMemo } from 'react'
import { Section } from '../Section'
import { InspectorBody, InspectorPrimaryContent, InspectorSecondaryContent } from '../article'
import { UpdateTagDescription, UpdateTagName } from '../changes/tag'
import { InlineField, InlineFieldGroup, InlineLabel } from '../inline-fieldset'
import { useDispatchChange } from '../useDispatchChange'
import {
  UpdateExternalDocumentationDescription,
  UpdateExternalDocumentationURL
} from '../changes/externaldocumentation'
import { escapeReferenceToken } from '@criteria/json-pointer'
import {
  UpdateOperationDeprecated,
  UpdateOperationDescription,
  UpdateOperationOperationID,
  UpdateOperationSummary
} from '../changes/operation'

export function EndpointOverview({ endpoint }: { endpoint: Endpoint }) {
  const dispatchChange = useDispatchChange()
  const debouncedDispatchChange = useMemo(() => debounce(dispatchChange), [dispatchChange])

  return (
    <InspectorBody split>
      <InspectorPrimaryContent>
        <Section className="mt-6">
          <Subheading className="flex min-h-10 items-center">General Information</Subheading>
          <InlineFieldGroup className="mt-3">
            <InlineField>
              <InlineLabel>Summary</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  placeholder="Add a name…"
                  className="text-sm"
                  value={endpoint.operation.summary}
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateOperationSummary(endpoint.operationRef, value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            <InlineField>
              <InlineLabel>Description</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  multiline
                  placeholder="Add a description…"
                  className="text-sm"
                  value={endpoint.operation.description}
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateOperationDescription(endpoint.operationRef, value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
          </InlineFieldGroup>
        </Section>
      </InspectorPrimaryContent>
      <InspectorSecondaryContent className="space-y-12 py-8">
        <Section>
          <Subheading className="flex min-h-10 items-center">Lifecycle</Subheading>
          <InlineFieldGroup>
            <SwitchField>
              <InlineLabel>Deprecated</InlineLabel>
              <Switch
                checked={endpoint.operation.deprecated}
                onChange={(checked) => {
                  debouncedDispatchChange(new UpdateOperationDeprecated(endpoint.operationRef, checked))
                }}
              />
            </SwitchField>
          </InlineFieldGroup>
        </Section>
        <Section>
          <Subheading className="flex min-h-10 items-center">OpenAPI Details</Subheading>
          <InlineFieldGroup>
            <InlineField>
              <InlineLabel>Operation ID</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={endpoint.operation.tags?.join(', ')}
                  placeholder="Add an operation ID…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateOperationOperationID(endpoint.operationRef, value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            {/* <InlineField>
              <InlineLabel>Tags</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={endpoint.operation.operationId}
                  placeholder="Add tags…"
                  className="text-sm"
                  onChange={(value) => {
                    // debouncedDispatchChange(new UpdateOperationOperationID(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField> */}
          </InlineFieldGroup>
        </Section>
        <Section>
          <Subheading className="flex min-h-10 items-center">External Documentation</Subheading>
          <InlineFieldGroup>
            <InlineField>
              <InlineLabel>URL</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={endpoint.operation.externalDocs?.url}
                  placeholder="Add a URL…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(
                      new UpdateExternalDocumentationURL(`${endpoint.operationRef}/externalDocs`, value)
                    )
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            <InlineField>
              <InlineLabel>Description</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={endpoint.operation.externalDocs?.description}
                  placeholder="Add a description…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(
                      new UpdateExternalDocumentationDescription(`${endpoint.operationRef}/externalDocs`, value)
                    )
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
          </InlineFieldGroup>
        </Section>
      </InspectorSecondaryContent>
    </InspectorBody>
  )
}
